import { activities } from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'

export const selectActivitySchema = createSelectSchema(activities)

// scheduledAt + durationMinutes are picked up automatically by createInsertSchema.
// `participants` is a nested write handled by the router's many-to-many upsert config.
export const insertActivitySchema = createInsertSchema(activities)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    userId: true, // userId is auto-injected from authenticated user context
  })
  .extend({
    metadata: z.record(z.unknown()).optional(),
    participants: z.array(z.object({ userId: z.number() })).optional(),
  })

export const updateActivitySchema = insertActivitySchema.partial()

const activityParticipantSchema = z.object({
  id: z.number(),
  userId: z.number(),
  user: z.object({
    id: z.number(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
  }),
})

export const selectActivityWithUserSchema = selectActivitySchema.extend({
  user: z.object({
    id: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
  }),
  // Hydrated server-side when entityType='user' so feeds rendered for a
  // related entity (customer / opportunity) can label "about person X"
  // without a follow-up lookup. Null otherwise.
  subjectUser: z
    .object({
      id: z.number(),
      firstName: z.string(),
      lastName: z.string(),
    })
    .nullable()
    .optional(),
  participants: z.array(activityParticipantSchema).optional(),
})

export type Activity = z.infer<typeof selectActivitySchema>
export type ActivityWithUser = z.infer<typeof selectActivityWithUserSchema>
export type CreateActivity = z.infer<typeof insertActivitySchema>
export type UpdateActivity = z.infer<typeof updateActivitySchema>
export type ActivityParticipant = z.infer<typeof activityParticipantSchema>
